<script setup lang="ts">
import type ChatRoom from '@/types/ChatRoom.ts'
import { computed } from 'vue'
import { useGameStore } from '@/stores/gameStore.ts'
import { usePlayerRelationStore } from '@/stores/playerRelations.ts'
import { useChatStore } from '@/stores/chatStore.ts'
import { useAllianceStore } from '@/stores/allianceStore.ts'
import router from '@/router'

/**
 * Props
 */
const props = defineProps<{
  room: ChatRoom
}>()

/**
 * Stores
 */
const gameStore = useGameStore()
const playerRelationStore = usePlayerRelationStore()
const chatStore = useChatStore()
const allianceStore = useAllianceStore()

/**
 * Computed
 */
const roomImage = computed(() => {
  if (props.room.roomType === 'pm') {
    const players = props.room.data.players
    // Check which player is not current player (players is an array of player ids)
    const otherPlayerId = players.find((playerId: number) => playerId !== gameStore.player?.id)
    const otherPlayer = playerRelationStore.findRelationByPlayerId(otherPlayerId!)
    return otherPlayer?.profileImage || ''
  } else if (props.room.roomType === 'alliance') {
    const alliance = allianceStore.getAlliance(gameStore.player?.allianceId!)
    return alliance?.allianceImage || ''
  } else {
    return '' // TODO: Add room image for other types of rooms
  }
})

const hasUnreadMessages = computed(() => {
  const roomReadStatus = chatStore.roomReadStatus.get(props.room.id)
  return roomReadStatus?.unreadStatus || false
})

const setActiveRoom = () => {
  //use router to goto /communication/id
  chatStore.setActiveRoomId(props.room.id)
  router.push(`/communication/${props.room.id}`)
  if (hasUnreadMessages.value) {
    chatStore.setRoomAsRead(props.room.id)
  }
}
</script>

<template>
  <div class="relative">
    <img
      :src="roomImage ? 'https://wsrv.nl/?url=' + roomImage + '&w=56&h=56' : ''"
      @click="setActiveRoom"
      :class="{
        'rounded object-cover mt-2 w-9 h-9': true,
        'border border-slate-300': chatStore.activeRoomId === props.room.id,
        'cursor-pointer': chatStore.activeRoomId !== props.room.id
      }"
      alt="Chat"
    />
    <div
      v-if="hasUnreadMessages"
      class="absolute top-0 right-0 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold"
    >
      !
    </div>
  </div>
</template>

<style scoped lang="scss"></style>
